import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { api } from "../api/client";
import { EntityLogo } from "./EntityLogo";
import type { LinkedCharacter } from "../hooks/useAuth";

interface AccountData {
  account: { id: string };
  active_character_id: number | null;
  characters: LinkedCharacter[];
}

interface AddCharactersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddCharactersModal({ open, onOpenChange }: AddCharactersModalProps) {
  const qc = useQueryClient();

  const account = useQuery<AccountData>({
    queryKey: ["account"],
    queryFn: () => api<AccountData>("/account"),
    enabled: open,
  });

  const addCharacter = useMutation({
    mutationFn: async () => {
      return api<{ authorize_url: string }>("/account/characters", {
        method: "POST",
      });
    },
    onSuccess: (data) => {
      window.location.href = data.authorize_url;
    },
  });

  const removeCharacter = useMutation({
    mutationFn: async (characterId: number) => {
      return api<{ ok: boolean }>(`/account/characters/${characterId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account"] });
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });

  const chars = account.data?.characters ?? [];
  const onlyOneLeft = chars.length <= 1;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal__overlay" />
        <Dialog.Content className="modal__content add-characters" aria-describedby={undefined}>
          <Dialog.Title className="modal__title">
            <UserPlus size={16} aria-hidden /> Add Characters
          </Dialog.Title>
          {account.isLoading && <p className="add-characters__status">Loading…</p>}
          {account.isError && (
            <p className="add-characters__status">Failed to load characters.</p>
          )}
          {account.data && (
            <ul className="add-characters__list">
              {chars.map((c) => (
                <li key={c.character_id} className="add-characters__row">
                  <img
                    className="add-characters__avatar"
                    src={`https://images.evetech.net/characters/${c.character_id}/portrait?size=64`}
                    alt=""
                  />
                  <div className="add-characters__row-name">
                    <div>{c.name}</div>
                    <div className="add-characters__row-corp">
                      <EntityLogo type="corp" id={c.corporation_id} size={16} />{" "}
                      {c.corporation_name ?? `Corp ${c.corporation_id}`}
                      {c.alliance_id != null && (
                        <>
                          {" · "}
                          <EntityLogo type="alliance" id={c.alliance_id} size={16} />{" "}
                          {c.alliance_name ?? `Alliance ${c.alliance_id}`}
                        </>
                      )}
                    </div>
                  </div>
                  {!c.esi_token_ok && (
                    <span
                      className="add-characters__token-warn"
                      title="ESI token expired — re-authorize to refresh"
                    >
                      ESI token expired
                    </span>
                  )}
                  <button
                    type="button"
                    className="modal__btn modal__btn--danger"
                    disabled={onlyOneLeft || removeCharacter.isPending}
                    onClick={() => removeCharacter.mutate(c.character_id)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="modal__actions">
            <button
              type="button"
              className="modal__btn"
              onClick={() => onOpenChange(false)}
            >
              Close
            </button>
            <button
              type="button"
              className="modal__btn modal__btn--primary"
              disabled={addCharacter.isPending}
              onClick={() => addCharacter.mutate()}
            >
              {addCharacter.isPending ? "Redirecting…" : "Re-authorize / Add character"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
